import { Controller } from "@hotwired/stimulus"

// While a feed is open, Substance is asked now and then what has arrived
// above the top post, and the new posts are let in at the top of the list,
// each settling into place. If the member has scrolled down to read, the page
// is held exactly where it is - the arrivals go in above, out of sight - and a
// small pill says how many wait; a tap brings them into view. Nothing a member
// is reading ever moves under them.
//
//   data-fresh-url-value    where to ask (feed#fresh)
//   data-fresh-for-value    which feed: home, explore, circle:<slug>
//   data-fresh-since-value  the slug of the post at the top of the page
//   data-fresh-arrived-value  the slug of a post just written, to settle in
//   data-fresh-one-new-value  what the pill says of one post ("1 new post")
//   data-fresh-many-new-value  and of more, with %{count} where the number goes
export default class extends Controller {
  static targets = ["list", "pill", "count"]
  static values = {
    url: String,
    for: { type: String, default: "home" },
    since: String,
    arrived: String,
    every: { type: Number, default: 30000 },
    oneNew: { type: String, default: "1 new post" },
    manyNew: { type: String, default: "%{count} new posts" }
  }

  connect() {
    this.waiting = 0
    this.settleArrival()
    this.schedule()
    this.onVisible = () => { if (document.visibilityState === "visible") this.look() }
    this.onScroll = () => { if (this.waiting && window.scrollY < 40) this.arrived() }
    document.addEventListener("visibilitychange", this.onVisible)
    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    clearTimeout(this.timer)
    document.removeEventListener("visibilitychange", this.onVisible)
    window.removeEventListener("scroll", this.onScroll)
  }

  schedule() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.look(), this.everyValue)
  }

  // Asks, and lets in whatever came. Also called by pull_controller.
  async look() {
    if (document.visibilityState !== "visible" || !this.hasListTarget) return this.schedule()

    try {
      const url = `${this.urlValue}?for=${encodeURIComponent(this.forValue)}&since=${encodeURIComponent(this.sinceValue)}`
      const response = await fetch(url, { headers: { Accept: "text/html" } })
      if (response.status === 200) this.letIn(await response.text())
    } catch { /* offline, perhaps; we ask again later */ }

    this.schedule()
    return true
  }

  // The new articles go in above the first, newest first, skipping any the
  // page already has (the member's own post, say, that came with a reload).
  letIn(html) {
    const template = document.createElement("template")
    template.innerHTML = html
    const have = new Set([...this.listTarget.querySelectorAll("[data-slug]")].map((el) => el.dataset.slug))
    const articles = [...template.content.querySelectorAll("article[data-slug]")].filter((a) => !have.has(a.dataset.slug))
    if (articles.length === 0) return

    const reading = window.scrollY > 40
    const before = this.listTarget.getBoundingClientRect().top

    articles.reverse().forEach((article) => {
      this.settle(article)
      this.listTarget.prepend(article)
    })
    // Newest at the top settles first; the rest follow a beat apart.
    articles.reverse().forEach((article, i) => article.style.setProperty("--settle", `${Math.min(i, 6) * 90}ms`))

    this.sinceValue = articles[0].dataset.slug

    if (reading) {
      // Hold the page where the reader had it: what went in above is as tall
      // as the list grew, so the window moves down by exactly that much.
      window.scrollBy({ top: this.listTarget.getBoundingClientRect().top - before, behavior: "instant" })
      this.waiting += articles.length
      this.show(this.waiting)
    }
  }

  // The pill: how many wait above, and a tap to go up to them.
  show(count) {
    if (!this.hasPillTarget) return
    this.pillTarget.hidden = count <= 0
    if (count > 0) this.countTarget.textContent = count === 1 ? this.oneNewValue : this.manyNewValue.replace("%{count}", count)
  }

  refresh(event) {
    event?.preventDefault()
    const top = this.listTarget.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" })
    this.arrived()
  }

  arrived() {
    this.waiting = 0
    this.show(0)
  }

  // A post the member has just written settles in like an arrival, so the
  // page it lands on says, without a word, that it is there.
  settleArrival() {
    if (!this.arrivedValue || !this.hasListTarget) return
    const article = this.listTarget.querySelector(`article[data-slug="${CSS.escape(this.arrivedValue)}"]`)
    if (article) this.settle(article)
    this.arrivedValue = ""
  }

  // The settling is a class for as long as it plays and no longer: an animation
  // that stayed would hold the article's opacity against anything after it,
  // the fade of a disappearing post above all.
  settle(article) {
    article.classList.add("arriving")
    article.addEventListener("animationend", () => {
      article.classList.remove("arriving")
      article.style.removeProperty("--settle")
    }, { once: true })
  }
}
